import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableWrapper,
} from '../../../services/settings/appStyle/global.style';
import {
  DeleteConfirmationModal,
  Loader,
  Table,
  IconButton as IconButtonWithText,
} from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { AddPlusIcon, DeleteIcon, EditIcon } from '../../../Components/icons';
import {
  templateList,
  removeTemplate,
  getTemplate,
} from '../../../services/bioplus-services/emailTemplate';
import { UserContainer } from '../UserV2/user.style';
import CreateTemplateModal from './modals/createTemplateModal';
import { LoadingContent, LoadingModal } from './emailTemplate.style';

const EmailTemplateList = () => {
  const [deleteTemplate, setDeleteTemplate] = useState(undefined);
  const [editTemplateId, setEditTemplateId] = useState(null);
  const [isCreateTemplate, setIsCreateTemplate] = useState(false);

  const queryClient = useQueryClient();

  const emailTemplateList = useQuery(['emailTemplates'], () => templateList());
  const emailTemplateData = emailTemplateList?.data ?? [];

  // Fetch template data using useQuery - only runs when editTemplateId exists
  const templateQuery = useQuery(
    ['emailTemplate', editTemplateId],
    () => getTemplate({ templateId: editTemplateId }),
    {
      enabled: !!editTemplateId, // Only fetch when editTemplateId is set
      onError: (error) => {
        alert(`Error: ${error?.response?.data?.message || 'Failed to load template'}`);
        setEditTemplateId(null); // Clear on error
      },
    }
  );

  const deleteMutation = useMutation({
    mutationFn: (template) => {
      return removeTemplate({ templateId: template?._id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('emailTemplates');
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message}`);
    },
  });

  const handleRemoveTemplate = (template) => {
    deleteMutation.mutate(template);
    setDeleteTemplate(undefined);
  };

  const handleEditTemplate = (data) => {
    queryClient.removeQueries(['emailTemplate', data._id]);
    setEditTemplateId(data._id);
  };

  const handleCloseEditModal = () => {
    setEditTemplateId(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateTemplate(false);
  };

  const groupColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.template.title" defaultMessage="Template Title" />,
        accessor: 'title',
        width: '42%',
      },

      {
        Header: <IntlMessages id="table.col.template.subject" defaultMessage="Template Subject" />,
        accessor: 'subject',
        width: '42%',
      },

      {
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '15%',
      },
    ],
    []
  );

  const actionList = [
    (data) => (
      <IconButton onClick={() => handleEditTemplate(data)}>
        <EditIcon style={{ width: '22px', height: '22px' }} />
      </IconButton>
    ),
    (data) =>
      data?.isPrivate !== 1 && (
        <IconButton onClick={() => setDeleteTemplate(data)}>
          <DeleteIcon style={{ width: '22px', height: '22px' }} />
        </IconButton>
      ),
  ];

  if (emailTemplateList.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <StyledContainer>
        <UserContainer>
          <HeaderTitle>
            <IntlMessages id="module.email.template" />
          </HeaderTitle>

          <IconButtonWithText
            text={<IntlMessages id="create.email.template" />}
            icon={<AddPlusIcon />}
            onClick={() => {
              setIsCreateTemplate(true);
            }}
          />
        </UserContainer>
        <TableWrapper>
          <Table
            data={emailTemplateData}
            columns={groupColumns}
            hasFooter={false}
            actions={actionList}
          />
        </TableWrapper>

        {deleteTemplate && (
          <DeleteConfirmationModal
            onClose={() => setDeleteTemplate(undefined)}
            message={
              <IntlMessages
                id="group.delete.confirmation"
                defaultMessage={`Are you sure you want to delete the template "${deleteTemplate?.title}"?`}
                values={{ group: deleteTemplate?.title }}
              />
            }
            onConfirm={() => handleRemoveTemplate(deleteTemplate)}
          />
        )}
      </StyledContainer>

      {templateQuery.isLoading && editTemplateId && (
        <LoadingModal>
          <LoadingContent>
            <Loader loadingText="Loading template data..." />
          </LoadingContent>
        </LoadingModal>
      )}

      {isCreateTemplate && <CreateTemplateModal onClose={handleCloseCreateModal} />}

      {templateQuery.data && editTemplateId && !templateQuery.isLoading && (
        <CreateTemplateModal
          onClose={handleCloseEditModal}
          templateData={templateQuery.data}
          isEdit={true}
        />
      )}
    </>
  );
};

export default EmailTemplateList;
